import { useCallback, useEffect, useRef, useState } from "react";

import { useTheme } from "@mui/material/styles";
import {
  Badge,
  Box,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Popper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";

import MainCard from "components/MainCard";
import IconButton from "components/@extended/IconButton";
import Transitions from "components/@extended/Transitions";

import { Message2, Notification } from "iconsax-react";
import Avatar from "components/@extended/Avatar";

import { ThemeMode } from "types/config";
import timeAgo, { getTimeOnly } from "utils/timeAgo";
import api from "api/api";
import serverCall from "api/serverCall";
import { useFirebaseContext } from "contexts/FirebaseContext";
import { throttle } from "lodash";
import { useWebsocketContext } from "contexts/WebSocketContext";

const actionSX = {
  mt: "6px",
  ml: 1,
  top: "auto",
  right: "auto",
  alignSelf: "flex-start",
  transform: "none",
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const NotificationPage = () => {
  const theme = useTheme();
  const { isLoggedIn } = useFirebaseContext();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const anchorRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unseenCount, setUnseenCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const { userLoginStatus, orderStatus } = useWebsocketContext();

  const handleToggle = (e: any) => {
    setOpen((prevOpen) => !prevOpen);
    if (!open) {
      setUnseenCount(0);
      localStorage.setItem(
        "lastSeenNotificationTime",
        new Date().toISOString()
      );
    }
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const fetchNotification = async (pageNumber: number) => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      const response: any = await serverCall.get(
        api.getNotifications(pageNumber)
      );
      const newNotifications = response.searchResponse || [];
      const totalRecords = response.totalNoRecords || 0;
      const pageSize = response.pageSize || 10;

      setNotifications((prev) => [...prev, ...newNotifications]);
      setHasMore((pageNumber + 1) * pageSize < totalRecords);

      const lastSeen = localStorage.getItem("lastSeenNotificationTime");
      if (lastSeen) {
        const unseen = newNotifications.filter(
          (n: any) => new Date(n.timestamp) > new Date(lastSeen)
        ).length;
        setUnseenCount((prev) => prev + unseen);
      } else {
        setUnseenCount((prev) => prev + newNotifications.length);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const throttledHandleScroll = useCallback(
    throttle(() => {
      if (!scrollRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20 && !loading) {
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        fetchNotification(nextPage);
      }
    }, 300), // 300ms throttle delay (tweak as needed)
    [loading, hasMore]
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (scrollRef.current && open) {
        scrollRef.current.addEventListener("scroll", throttledHandleScroll);
      }
    });

    if (scrollRef.current) {
      scrollRef.current.addEventListener("scroll", throttledHandleScroll);
    } else {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", throttledHandleScroll);
      }
      observer.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (isLoggedIn) {
      setNotifications([]);
      pageRef.current = 0;
      setHasMore(true);
      fetchNotification(0);
    }
  }, [isLoggedIn, userLoginStatus, orderStatus]);

  useEffect(() => {
    return () => {
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    if (open) {
      localStorage.setItem(
        "lastSeenNotificationTime",
        new Date().toISOString()
      );
    }
  }, [open]);

  const iconBackColorOpen =
    theme.palette.mode === ThemeMode.DARK ? "secondary.200" : "secondary.200";
  const iconBackColor =
    theme.palette.mode === ThemeMode.DARK
      ? "background.default"
      : "secondary.100";

  const NotificationInfoItem = ({ message }: { message: string }) => (
    <ListItemButton
      disableRipple
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 1.5,
        my: 1.5,
        width: "364px",
        // height: "400px",
        border: `1px solid ${theme.palette.divider}`,
        "&:hover": {
          backgroundColor: "action.hover",
          borderColor: theme.palette.primary.light,
        },
      }}
    >
      <Typography variant="body2" textAlign="center">
        {message}
      </Typography>
    </ListItemButton>
  );
  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        variant="light"
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        size="large"
        sx={{
          color: "secondary.main",
          bgcolor: open ? iconBackColorOpen : iconBackColor,
          p: 1,
        }}
      >
        <Badge
          badgeContent={unseenCount > 99 ? "99+" : unseenCount}
          color="success"
          sx={{ "& .MuiBadge-badge": { top: 2, right: 4 } }}
        >
          <Notification variant="Bold" />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [matchesXs ? -5 : 0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={matchesXs ? "top" : "top-right"}
            sx={{ overflow: "hidden" }}
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                borderRadius: 1.5,
                width: "100%",
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down("md")]: {
                  maxWidth: 285,
                },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="h5">Notifications</Typography>
                  </Stack>
                  <Box
                    ref={scrollRef}
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      mt: 2,
                      pr: 1,
                    }}
                  >
                    <List
                      component="nav"
                      sx={{
                        "& .MuiListItemButton-root": {
                          p: 1.5,
                          my: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            bgcolor: "primary.lighter",
                            borderColor: theme.palette.primary.light,
                          },
                          "& .MuiListItemSecondaryAction-root": {
                            ...actionSX,
                            position: "relative",
                          },
                        },
                      }}
                    >
                      {notifications?.map((notification: any, i: number) => {
                        return (
                          <ListItemButton
                            key={i}
                            onClick={() => {}}
                            disableRipple
                            sx={{
                              cursor: "default",
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar type="filled">
                                <Message2 size={20} variant="Bold" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="h6">
                                  {notification?.message
                                    ? notification?.message
                                    : "_"}
                                </Typography>
                              }
                              secondary={timeAgo(notification.timestamp)}
                            />
                            <ListItemSecondaryAction>
                              <Typography variant="caption" noWrap>
                                {getTimeOnly(notification.timestamp)}
                              </Typography>
                            </ListItemSecondaryAction>
                          </ListItemButton>
                        );
                      })}
                      {loading && (
                        <NotificationInfoItem message="Loading more..." />
                      )}
                      {!hasMore && notifications.length > 0 && (
                        <NotificationInfoItem message="You’re all caught up 🎉" />
                      )}
                    </List>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default NotificationPage;
